/**
 * EP-88 T108b — Firebase Analytics RUM for real-user monitoring.
 *
 * Initializes Firebase Analytics (page_view events + web-vitals forwarding)
 * using the same Firebase project that already serves FCM push and Hosting.
 *
 * Guard contract: if ANY of the five required VITE_FIREBASE_* env vars is
 * absent, init is skipped entirely — no crash, no network noise. This mirrors
 * the pattern used by the PostHog integration (see analytics.ts line 130-135).
 *
 * No PII is ever passed; Firebase Analytics receives only the defaults it
 * collects (page URLs + timing). The `page_view` event is sent on every
 * history change so SPA navigation is tracked.
 *
 * Env vars required (all build-time VITE_* — injected by deploy.yml):
 *   VITE_FIREBASE_API_KEY
 *   VITE_FIREBASE_AUTH_DOMAIN
 *   VITE_FIREBASE_PROJECT_ID
 *   VITE_FIREBASE_APP_ID
 *   VITE_FIREBASE_MEASUREMENT_ID   ← Analytics data stream measurement ID (G-...)
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getAnalytics, isSupported, logEvent, type Analytics } from 'firebase/analytics'

// ---------------------------------------------------------------------------
// Config — build-time VITE_* vars, all optional (guard below)
// ---------------------------------------------------------------------------

interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  appId: string
  measurementId: string
}

function readConfig(): FirebaseConfig | null {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY as string | undefined
  const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined
  const appId = import.meta.env.VITE_FIREBASE_APP_ID as string | undefined
  const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID as string | undefined

  if (!apiKey || !authDomain || !projectId || !appId || !measurementId) {
    return null
  }

  return { apiKey, authDomain, projectId, appId, measurementId }
}

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let _analytics: Analytics | null = null
let _initialized = false

/** Exported for tests: reset module state between specs. */
export function __resetFirebaseRumForTests(): void {
  _analytics = null
  _initialized = false
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

/**
 * Initialize Firebase Analytics for RUM.
 *
 * Safe to call multiple times — subsequent calls are no-ops.
 * Safe to call when the Firebase web config is absent — returns without
 * throwing (no crash, no console error).
 *
 * Async because `isSupported()` (the official Firebase guard for environments
 * like WebWorkers or bfcache-ineligible contexts) returns a Promise.
 * The caller in main.tsx fires-and-forgets (.catch(() => {})) so the app
 * startup path is not blocked.
 */
export async function initFirebaseRum(): Promise<void> {
  if (_initialized) return
  _initialized = true

  const config = readConfig()
  if (!config) {
    // No Firebase config provisioned — silent no-op. This is expected in
    // local dev and CI without the VITE_FIREBASE_* secrets set.
    return
  }

  // Firebase Analytics is not supported in all environments (e.g. Node SSR,
  // certain browsers). Check before initializing.
  const supported = await isSupported()
  if (!supported) return

  // Reuse an existing Firebase app if one was already initialized (e.g. by
  // the Capacitor Firebase Messaging plugin), otherwise create a new one.
  // The app name 'flowrent-rum' is distinct from the Capacitor default so
  // we never conflict with the FCM app.
  let app: FirebaseApp
  const existingApps = getApps()
  const existing = existingApps.find((a) => a.name === 'flowrent-rum')
  if (existing) {
    app = existing
  } else {
    app = initializeApp(config, 'flowrent-rum')
  }

  try {
    _analytics = getAnalytics(app)
  } catch {
    // Non-fatal: getAnalytics can throw in edge cases (e.g. duplicate
    // analytics initialization). Swallow so the app never crashes.
    _analytics = null
  }
}

// ---------------------------------------------------------------------------
// Page-view tracking for SPA navigation
// ---------------------------------------------------------------------------

/**
 * Log a `page_view` event to Firebase Analytics.
 *
 * Call this on each React Router navigation. No-ops gracefully if Analytics
 * was not initialized (config absent or environment unsupported).
 *
 * Only `page_path` and `page_title` are sent — no PII beyond what Firebase
 * already collects by default (IP anonymization is on by default in GA4).
 */
export function logPageView(path: string, title?: string): void {
  if (!_analytics) return
  logEvent(_analytics, 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
  })
}

// ---------------------------------------------------------------------------
// Exported for tests — read current analytics instance
// ---------------------------------------------------------------------------

/** Returns the initialized Analytics instance, or null if not initialized. */
export function getAnalyticsInstance(): Analytics | null {
  return _analytics
}
